import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Send, Loader2 } from "lucide-react";
import { Reveal, Overline, KineticLines } from "@/components/motion";
import { sendContactMessage } from "@/lib/api";
import { CONTACT_INFO } from "@/data/demo";
import { useSiteContent } from "@/lib/content";

const EMPTY = { nom: "", prenom: "", email: "", telephone: "", objet: "", message: "", website: "" };

const validate = (form) => {
  const errors = {};
  if (!form.nom.trim()) errors.nom = "Le nom est requis.";
  if (!form.prenom.trim()) errors.prenom = "Le prénom est requis.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Adresse email invalide.";
  if (form.telephone && !/^[+0-9 ().-]{8,20}$/.test(form.telephone)) errors.telephone = "Numéro de téléphone invalide.";
  if (!form.objet.trim()) errors.objet = "L'objet est requis.";
  if (form.message.trim().length < 5) errors.message = "Le message est trop court.";
  return errors;
};

const inputClass =
  "w-full bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all placeholder:text-white/25";

const Field = ({ label, error, children, testId }) => (
  <div data-testid={testId}>
    <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-xs text-rouge-hover">{error}</p>}
  </div>
);

const Contact = () => {
  const c = useSiteContent();
  const titleOverride = c("contact.title", "");
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ ...EMPTY, objet: searchParams.get("objet") || "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Veuillez corriger les champs indiqués.");
      return;
    }
    setSending(true);
    try {
      const res = await sendContactMessage(form);
      toast.success(res.message || "Message envoyé avec succès.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="px-6 md:px-12 lg:px-24 pt-40 pb-24 md:pb-32 min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* INFOS */}
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>{c("contact.overline", "Une question, un projet ?")}</Overline>
          </Reveal>
          <KineticLines
            lines={
              titleOverride
                ? titleOverride.split("\n").map((l) => l.trim()).filter(Boolean).map((l, i) => (
                    <span key={i} className={i === 1 ? "text-gold" : "text-white"}>{l}</span>
                  ))
                : [
                    <span key="a" className="text-white">CONTACTEZ-</span>,
                    <span key="b" className="text-gold">NOUS</span>,
                  ]
            }
            className="mt-5 font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tight"
          />
          <Reveal delay={0.3}>
            <p className="mt-7 text-white/65 leading-relaxed max-w-md">
              {c("contact.subtitle", "Stages, matchs amicaux, partenariats ou boutique : écrivez-nous, nous répondons rapidement à toutes les demandes.")}
            </p>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="mt-12 space-y-6">
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center border border-gold/30 text-gold shrink-0">
                  <Mail size={17} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Email</p>
                  <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email" className="text-white hover:text-gold transition-colors">
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
              {CONTACT_INFO.telephones.map((t) => (
                <div key={t.number} className="flex items-center gap-4">
                  <span className="w-11 h-11 flex items-center justify-center border border-gold/30 text-gold shrink-0">
                    <Phone size={17} />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Téléphone — {t.name}</p>
                    <a href={`tel:${t.number.replace(/\s/g, "")}`} data-testid={`contact-phone-${t.name.toLowerCase()}`} className="text-white hover:text-gold transition-colors">
                      {t.number}
                    </a>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 flex items-center justify-center border border-gold/30 text-gold shrink-0">
                  <MapPin size={17} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Zone d'activité</p>
                  <p className="text-white">{CONTACT_INFO.zone}</p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <a
                href={CONTACT_INFO.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-social-instagram"
                className="flex items-center gap-3 border border-white/15 hover:border-gold text-white/60 hover:text-gold transition-colors px-5 py-2.5"
              >
                <Instagram size={16} />
                <span className="text-xs font-bold tracking-[0.15em] uppercase">@3m.matchpro</span>
              </a>
            </div>
          </Reveal>
        </div>

        {/* FORM */}
        <Reveal delay={0.2} className="lg:col-span-7">
          {sent ? (
            <div data-testid="contact-success" className="bg-noir-surface border border-gold/30 p-10 md:p-14 text-center">
              <p className="font-display uppercase text-4xl md:text-5xl tracking-wide text-gold">Message envoyé</p>
              <p className="mt-4 text-white/65 leading-relaxed">
                Nous avons bien reçu votre demande. Un email de confirmation vient de vous être adressé —
                l'équipe 3M Match Pro reviendra vers vous prochainement.
              </p>
              <button
                data-testid="contact-new-message"
                onClick={() => setSent(false)}
                className="mt-8 px-8 py-3.5 border-2 border-gold text-gold text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="bg-noir-surface border border-noir-border p-8 md:p-10"
          >
            {/* Honeypot anti-spam — invisible pour les humains */}
            <input
              type="text"
              data-testid="input-website-honeypot"
              value={form.website}
              onChange={update("website")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Nom *" error={errors.nom} testId="field-nom">
                <input
                  data-testid="input-nom"
                  type="text"
                  value={form.nom}
                  onChange={update("nom")}
                  placeholder="Votre nom"
                  className={inputClass}
                />
              </Field>
              <Field label="Prénom *" error={errors.prenom} testId="field-prenom">
                <input
                  data-testid="input-prenom"
                  type="text"
                  value={form.prenom}
                  onChange={update("prenom")}
                  placeholder="Votre prénom"
                  className={inputClass}
                />
              </Field>
              <Field label="Email *" error={errors.email} testId="field-email">
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="vous@exemple.fr"
                  className={inputClass}
                />
              </Field>
              <Field label="Téléphone" error={errors.telephone} testId="field-telephone">
                <input
                  data-testid="input-telephone"
                  type="tel"
                  value={form.telephone}
                  onChange={update("telephone")}
                  placeholder="06 12 34 56 78"
                  className={inputClass}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Objet *" error={errors.objet} testId="field-objet">
                  <input
                    data-testid="input-objet"
                    type="text"
                    value={form.objet}
                    onChange={update("objet")}
                    placeholder="Ex : Demande de devis — Match amical"
                    className={inputClass}
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Message *" error={errors.message} testId="field-message">
                  <textarea
                    data-testid="input-message"
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Décrivez votre demande…"
                    className={`${inputClass} resize-y`}
                  />
                </Field>
              </div>
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="mt-8 inline-flex items-center gap-3 px-10 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_20px_rgba(230,0,0,0.3)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Envoi en cours…" : "Envoyer"}
            </button>
            <p className="mt-4 text-white/30 text-xs">
              Votre message est transmis directement à notre équipe par email et enregistré de manière sécurisée.
            </p>
          </form>
          )}
        </Reveal>
      </div>
    </div>
  );
};

export default Contact;
